import { ThunkResult } from 'redux/types';
import * as errorDispatch from 'redux/ducks/error';
import { ErrorActions } from 'redux/ducks/error';
import {
  MonthlyReportsApiResponse,
  MonthlyReportsActions,
  MonthlyReportsActionTypes,
  MonthlyReport,
  MonthlyReportsQueryParams,
  MonthlyReportsState,
  SendSummary
} from 'redux/ducks/monthlyReports/types';
import { fetchMonthlyReports, sendMonthlyReportEmail } from './api';

export const initialState: MonthlyReportsState = {
  reports: [],
  total: 0,
  limit: 25,
  skip: 0,
  hiddenColumns: [],
  status: 'init'
};

export default function reducer(
  state: MonthlyReportsState = initialState,
  action: MonthlyReportsActions
): MonthlyReportsState {
  switch (action.type) {
    case MonthlyReportsActionTypes.GET_MONTHLY_REPORTS_REQUEST:
      return { ...state, status: 'loading' };
    case MonthlyReportsActionTypes.GET_MONTHLY_REPORTS:
      return {
        ...state,
        reports: action.payload.data,
        total: action.payload.total,
        limit: action.payload.limit,
        skip: action.payload.skip,
        status: 'loaded'
      };
    case MonthlyReportsActionTypes.GET_MONTHLY_REPORTS_FAILURE:
      return {
        ...state,
        status: 'loaded'
      };
    case MonthlyReportsActionTypes.SET_MONTHLY_REPORTS_HIDDEN_COLUMNS:
      return {
        ...state,
        hiddenColumns: action.payload
      };
    default:
      return state;
  }
}

export function getReports(
  params: MonthlyReportsQueryParams
): ThunkResult<Promise<MonthlyReportsApiResponse | null>, MonthlyReportsActions | ErrorActions> {
  return async (dispatch) => {
    try {
      dispatch({ type: MonthlyReportsActionTypes.GET_MONTHLY_REPORTS_REQUEST });
      const response = await fetchMonthlyReports(params);

      dispatch({
        type: MonthlyReportsActionTypes.GET_MONTHLY_REPORTS,
        payload: response
      });

      return response;
    } catch (error: unknown) {
      dispatch({ type: MonthlyReportsActionTypes.GET_MONTHLY_REPORTS_FAILURE });
      dispatch(errorDispatch.displayError(error as Error));
      return null;
    }
  };
}

export function setHiddenColumns(columns: string[]): MonthlyReportsActions {
  return {
    type: MonthlyReportsActionTypes.SET_MONTHLY_REPORTS_HIDDEN_COLUMNS,
    payload: columns
  };
}

export function sendReportEmail(
  accountId: string,
  reportId: MonthlyReport['id']
): ThunkResult<Promise<SendSummary | null>, MonthlyReportsActions | ErrorActions> {
  return async (dispatch) => {
    try {
      dispatch({ type: MonthlyReportsActionTypes.SEND_MONTHLY_REPORT_EMAIL_REQUEST });
      const response = await sendMonthlyReportEmail(accountId, reportId);

      if (response.failed > 0 || response.error) {
        const rowErrors = response.results
          .filter((result) => !!result.error)
          .map((result) => result.error);
        const message = response.error || rowErrors.join(', ') || 'Failed to send report email.';

        dispatch({ type: MonthlyReportsActionTypes.SEND_MONTHLY_REPORT_EMAIL_FAILURE });
        dispatch(errorDispatch.displayError(new Error(message)));
        return null;
      }

      dispatch({ type: MonthlyReportsActionTypes.SEND_MONTHLY_REPORT_EMAIL_SUCCESS });
      return response;
    } catch (error: unknown) {
      dispatch({ type: MonthlyReportsActionTypes.SEND_MONTHLY_REPORT_EMAIL_FAILURE });
      dispatch(errorDispatch.displayError(error as Error));
      return null;
    }
  };
}

export * from './types';
export * from './selectors';
